import type { ToolMessage } from '@xsai/shared-chat'

import type { AgentHookRegistry, ChatHookRegistry } from '../contracts/hook-types'
import type { ChatStreamEventContext, StreamingAssistantMessage } from '../types/chat'

export function createChatHooks(): ChatHookRegistry {
  const onBeforeMessageComposedHooks: Array<(message: string, context: Omit<ChatStreamEventContext, 'composedMessage'>) => Promise<void>> = []
  const onAfterMessageComposedHooks: Array<(message: string, context: ChatStreamEventContext) => Promise<void>> = []
  const onBeforeSendHooks: Array<(message: string, context: ChatStreamEventContext) => Promise<void>> = []
  const onAfterSendHooks: Array<(message: string, context: ChatStreamEventContext) => Promise<void>> = []
  const onTokenLiteralHooks: Array<(literal: string, context: ChatStreamEventContext) => Promise<void>> = []
  const onTokenSpecialHooks: Array<(special: string, context: ChatStreamEventContext) => Promise<void>> = []
  const onStreamEndHooks: Array<(context: ChatStreamEventContext) => Promise<void>> = []
  const onAssistantResponseEndHooks: Array<(message: string, context: ChatStreamEventContext) => Promise<void>> = []
  const onAssistantMessageHooks: Array<(message: StreamingAssistantMessage, messageText: string, context: ChatStreamEventContext) => Promise<void>> = []
  const onChatTurnCompleteHooks: Array<(chat: { output: StreamingAssistantMessage, outputText: string, toolCalls: ToolMessage[] }, context: ChatStreamEventContext) => Promise<void>> = []

  function onBeforeMessageComposed(cb: (message: string, context: Omit<ChatStreamEventContext, 'composedMessage'>) => Promise<void>) {
    onBeforeMessageComposedHooks.push(cb)
    return () => {
      const index = onBeforeMessageComposedHooks.indexOf(cb)
      if (index >= 0)
        onBeforeMessageComposedHooks.splice(index, 1)
    }
  }

  function onAfterMessageComposed(cb: (message: string, context: ChatStreamEventContext) => Promise<void>) {
    onAfterMessageComposedHooks.push(cb)
    return () => {
      const index = onAfterMessageComposedHooks.indexOf(cb)
      if (index >= 0)
        onAfterMessageComposedHooks.splice(index, 1)
    }
  }

  function onBeforeSend(cb: (message: string, context: ChatStreamEventContext) => Promise<void>) {
    onBeforeSendHooks.push(cb)
    return () => {
      const index = onBeforeSendHooks.indexOf(cb)
      if (index >= 0)
        onBeforeSendHooks.splice(index, 1)
    }
  }

  function onAfterSend(cb: (message: string, context: ChatStreamEventContext) => Promise<void>) {
    onAfterSendHooks.push(cb)
    return () => {
      const index = onAfterSendHooks.indexOf(cb)
      if (index >= 0)
        onAfterSendHooks.splice(index, 1)
    }
  }

  function onTokenLiteral(cb: (literal: string, context: ChatStreamEventContext) => Promise<void>) {
    onTokenLiteralHooks.push(cb)
    return () => {
      const index = onTokenLiteralHooks.indexOf(cb)
      if (index >= 0)
        onTokenLiteralHooks.splice(index, 1)
    }
  }

  function onTokenSpecial(cb: (special: string, context: ChatStreamEventContext) => Promise<void>) {
    onTokenSpecialHooks.push(cb)
    return () => {
      const index = onTokenSpecialHooks.indexOf(cb)
      if (index >= 0)
        onTokenSpecialHooks.splice(index, 1)
    }
  }

  function onStreamEnd(cb: (context: ChatStreamEventContext) => Promise<void>) {
    onStreamEndHooks.push(cb)
    return () => {
      const index = onStreamEndHooks.indexOf(cb)
      if (index >= 0)
        onStreamEndHooks.splice(index, 1)
    }
  }

  function onAssistantResponseEnd(cb: (message: string, context: ChatStreamEventContext) => Promise<void>) {
    onAssistantResponseEndHooks.push(cb)
    return () => {
      const index = onAssistantResponseEndHooks.indexOf(cb)
      if (index >= 0)
        onAssistantResponseEndHooks.splice(index, 1)
    }
  }

  function onAssistantMessage(cb: (message: StreamingAssistantMessage, messageText: string, context: ChatStreamEventContext) => Promise<void>) {
    onAssistantMessageHooks.push(cb)
    return () => {
      const index = onAssistantMessageHooks.indexOf(cb)
      if (index >= 0)
        onAssistantMessageHooks.splice(index, 1)
    }
  }

  function onChatTurnComplete(cb: (chat: { output: StreamingAssistantMessage, outputText: string, toolCalls: ToolMessage[] }, context: ChatStreamEventContext) => Promise<void>) {
    onChatTurnCompleteHooks.push(cb)
    return () => {
      const index = onChatTurnCompleteHooks.indexOf(cb)
      if (index >= 0)
        onChatTurnCompleteHooks.splice(index, 1)
    }
  }

  function clearHooks() {
    onBeforeMessageComposedHooks.length = 0
    onAfterMessageComposedHooks.length = 0
    onBeforeSendHooks.length = 0
    onAfterSendHooks.length = 0
    onTokenLiteralHooks.length = 0
    onTokenSpecialHooks.length = 0
    onStreamEndHooks.length = 0
    onAssistantResponseEndHooks.length = 0
    onAssistantMessageHooks.length = 0
    onChatTurnCompleteHooks.length = 0
  }

  async function emitBeforeMessageComposedHooks(message: string, context: Omit<ChatStreamEventContext, 'composedMessage'>) {
    for (const hook of onBeforeMessageComposedHooks)
      await hook(message, context)
  }

  async function emitAfterMessageComposedHooks(message: string, context: ChatStreamEventContext) {
    for (const hook of onAfterMessageComposedHooks)
      await hook(message, context)
  }

  async function emitBeforeSendHooks(message: string, context: ChatStreamEventContext) {
    for (const hook of onBeforeSendHooks)
      await hook(message, context)
  }

  async function emitAfterSendHooks(message: string, context: ChatStreamEventContext) {
    for (const hook of onAfterSendHooks)
      await hook(message, context)
  }

  async function emitTokenLiteralHooks(literal: string, context: ChatStreamEventContext) {
    for (const hook of onTokenLiteralHooks)
      await hook(literal, context)
  }

  async function emitTokenSpecialHooks(special: string, context: ChatStreamEventContext) {
    for (const hook of onTokenSpecialHooks)
      await hook(special, context)
  }

  async function emitStreamEndHooks(context: ChatStreamEventContext) {
    for (const hook of onStreamEndHooks)
      await hook(context)
  }

  async function emitAssistantResponseEndHooks(message: string, context: ChatStreamEventContext) {
    for (const hook of onAssistantResponseEndHooks)
      await hook(message, context)
  }

  async function emitAssistantMessageHooks(message: StreamingAssistantMessage, messageText: string, context: ChatStreamEventContext) {
    for (const hook of onAssistantMessageHooks)
      await hook(message, messageText, context)
  }

  async function emitChatTurnCompleteHooks(chat: { output: StreamingAssistantMessage, outputText: string, toolCalls: ToolMessage[] }, context: ChatStreamEventContext) {
    for (const hook of onChatTurnCompleteHooks)
      await hook(chat, context)
  }

  return {
    onBeforeMessageComposed,
    onAfterMessageComposed,
    onBeforeSend,
    onAfterSend,
    onTokenLiteral,
    onTokenSpecial,
    onStreamEnd,
    onAssistantResponseEnd,
    onAssistantMessage,
    onChatTurnComplete,
    emitBeforeMessageComposedHooks,
    emitAfterMessageComposedHooks,
    emitBeforeSendHooks,
    emitAfterSendHooks,
    emitTokenLiteralHooks,
    emitTokenSpecialHooks,
    emitStreamEndHooks,
    emitAssistantResponseEndHooks,
    emitAssistantMessageHooks,
    emitChatTurnCompleteHooks,
    clearHooks,
  }
}

export function createAgentHooks<TContext, TAssistantMessage, TToolCall>(): AgentHookRegistry<TContext, TAssistantMessage, TToolCall> {
  const onBeforeMessageComposedHooks: Array<(message: string, context: Omit<TContext, 'composedMessage'>) => Promise<void>> = []
  const onAfterMessageComposedHooks: Array<(message: string, context: TContext) => Promise<void>> = []
  const onBeforeSendHooks: Array<(message: string, context: TContext) => Promise<void>> = []
  const onAfterSendHooks: Array<(message: string, context: TContext) => Promise<void>> = []
  const onTokenLiteralHooks: Array<(literal: string, context: TContext) => Promise<void>> = []
  const onTokenSpecialHooks: Array<(special: string, context: TContext) => Promise<void>> = []
  const onStreamEndHooks: Array<(context: TContext) => Promise<void>> = []
  const onAssistantResponseEndHooks: Array<(message: string, context: TContext) => Promise<void>> = []
  const onAssistantMessageHooks: Array<(message: TAssistantMessage, messageText: string, context: TContext) => Promise<void>> = []
  const onChatTurnCompleteHooks: Array<(chat: { output: TAssistantMessage, outputText: string, toolCalls: TToolCall[] }, context: TContext) => Promise<void>> = []

  function createSubscribe<T>(bucket: T[], cb: T) {
    bucket.push(cb)
    return () => {
      const index = bucket.indexOf(cb)
      if (index >= 0)
        bucket.splice(index, 1)
    }
  }

  function clearHooks() {
    onBeforeMessageComposedHooks.length = 0
    onAfterMessageComposedHooks.length = 0
    onBeforeSendHooks.length = 0
    onAfterSendHooks.length = 0
    onTokenLiteralHooks.length = 0
    onTokenSpecialHooks.length = 0
    onStreamEndHooks.length = 0
    onAssistantResponseEndHooks.length = 0
    onAssistantMessageHooks.length = 0
    onChatTurnCompleteHooks.length = 0
  }

  async function emitHooks<T extends any[]>(hooks: Array<(...args: T) => Promise<void>>, ...args: T) {
    for (const hook of hooks)
      await hook(...args)
  }

  return {
    onBeforeMessageComposed: cb => createSubscribe(onBeforeMessageComposedHooks, cb),
    onAfterMessageComposed: cb => createSubscribe(onAfterMessageComposedHooks, cb),
    onBeforeSend: cb => createSubscribe(onBeforeSendHooks, cb),
    onAfterSend: cb => createSubscribe(onAfterSendHooks, cb),
    onTokenLiteral: cb => createSubscribe(onTokenLiteralHooks, cb),
    onTokenSpecial: cb => createSubscribe(onTokenSpecialHooks, cb),
    onStreamEnd: cb => createSubscribe(onStreamEndHooks, cb),
    onAssistantResponseEnd: cb => createSubscribe(onAssistantResponseEndHooks, cb),
    onAssistantMessage: cb => createSubscribe(onAssistantMessageHooks, cb),
    onChatTurnComplete: cb => createSubscribe(onChatTurnCompleteHooks, cb),

    emitBeforeMessageComposedHooks: (message, context) => emitHooks(onBeforeMessageComposedHooks, message, context),
    emitAfterMessageComposedHooks: (message, context) => emitHooks(onAfterMessageComposedHooks, message, context),
    emitBeforeSendHooks: (message, context) => emitHooks(onBeforeSendHooks, message, context),
    emitAfterSendHooks: (message, context) => emitHooks(onAfterSendHooks, message, context),
    emitTokenLiteralHooks: (literal, context) => emitHooks(onTokenLiteralHooks, literal, context),
    emitTokenSpecialHooks: (special, context) => emitHooks(onTokenSpecialHooks, special, context),
    emitStreamEndHooks: context => emitHooks(onStreamEndHooks, context),
    emitAssistantResponseEndHooks: (message, context) => emitHooks(onAssistantResponseEndHooks, message, context),
    emitAssistantMessageHooks: (message, messageText, context) => emitHooks(onAssistantMessageHooks, message, messageText, context),
    emitChatTurnCompleteHooks: (chat, context) => emitHooks(onChatTurnCompleteHooks, chat, context),
    clearHooks,
  }
}
